import * as Sentry from "@sentry/react";
import classNames from "classnames";
import type React from "react";
import { useEffect } from "react";
import { useComponentState, useDebounce } from "../../utils/customHook.util";
import { Flex } from "./Flex";
import "./Autocomplete.css";
import Icon from "./Icon";
import Input from "./Input";
import Label from "./Label";

type AutoCompleteProps<T> = {
    label: string;
    placeholder?: string;
    results: (searchTerm: string) => Promise<T[]>;
    renderSingleResult: (singleResult: T) => React.ReactNode;
    onSelect: (selected: T | undefined) => void;
    initialSelection?: T;
    className?: string;
    resetButton?: boolean;
};

type AutoCompleteState<T> = {
    searchTerm: string;
    selected: T | undefined;
    results: T[];
    loading: boolean;
};

function Autocomplete<T extends { id: string }>(props: AutoCompleteProps<T>) {
    const {
        className,
        label,
        placeholder,
        results,
        renderSingleResult,
        initialSelection,
        onSelect,
        resetButton,
    } = props;
    const { state, setState } = useComponentState<AutoCompleteState<T>>({
        searchTerm: "",
        selected: initialSelection,
        results: [],
        loading: false,
    });
    const debouncedSearchTerm = useDebounce<string>(state.searchTerm, 250);

    async function loadResults(onTerm: string) {
        try {
            setState({ loading: false });

            // TODO: How to cancel the old search?
            const res = await results(onTerm);

            setState({ results: res, loading: false });
        } catch (error) {
            Sentry.captureException(error, {
                extra: { message: "Autocomplete result search failed" },
            });
        } finally {
            setState({ loading: false });
        }
    }

    useEffect(() => {
        loadResults(debouncedSearchTerm);
    }, [debouncedSearchTerm]);

    useEffect(() => {
        setState({ selected: initialSelection });
    }, [initialSelection]);

    function selectResultItem(item: T) {
        setState({ selected: item, searchTerm: "", results: [] });
        onSelect(item);
    }

    function reset() {
        setState({ selected: undefined });
        onSelect(undefined);
    }

    return (
        <Flex.Column gap="xsmall" className={classNames("autocomplete", className)}>
            {!state.selected && (
                <Input.PersistentTextField
                    placeholder={placeholder}
                    type="text"
                    label={label}
                    value={state.searchTerm}
                    onChange={(value) => {
                        setState({ searchTerm: value });
                    }}
                    readOnly={!!state.selected}
                />
            )}

            {state.selected && (
                <Flex.Row
                    justifyContent="space-between"
                    className="autocomplete__selection"
                >
                    <Flex.Column gap="small">
                        <Flex.Row gap="small">
                            {label && <Label.Small.N60>{label}</Label.Small.N60>}
                        </Flex.Row>
                        <Flex.Row
                            gap="small"
                            onClick={() => {
                                setState({ selected: undefined });
                            }}
                        >
                            {renderSingleResult(state.selected)}
                        </Flex.Row>
                    </Flex.Column>
                    {resetButton && (
                        <span className="autocomplete__selection-clear" onClick={reset}>
                            <Icon.Close.Medium />
                        </span>
                    )}
                </Flex.Row>
            )}
            {state.results.length !== 0 && (
                <div className="autocomplete__results">
                    {state.results.map((it) => (
                        <Flex.Row
                            key={it.id}
                            className="autocomplete__result-item"
                            gap="medium"
                            onClick={() => {
                                selectResultItem(it);
                            }}
                        >
                            {renderSingleResult(it)}
                        </Flex.Row>
                    ))}
                </div>
            )}
        </Flex.Column>
    );
}

export default Autocomplete;
